import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

const triggerHapticFeedback = (() => {
  let lastTriggeredAt = 0;

  return () => {
    const now = Date.now();
    if (now - lastTriggeredAt < 120) return;
    lastTriggeredAt = now;

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
        navigator.vibrate([15, 25, 15]);
      }
    } catch (_) {
      // Ignore vibration errors on unsupported browsers.
    }
  };
})();

if (typeof window !== 'undefined' && !window.__libernovoTriggerHapticFeedback) {
  window.__libernovoTriggerHapticFeedback = triggerHapticFeedback;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (!this.#checkFormValidity()) return;

    window.__libernovoTriggerHapticFeedback?.();

    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Checks if the form is valid when the user adds an item to cart.
   * Currently only checks the gift card recipient form.
   * @returns {boolean} - True if the form is valid, false otherwise.
   */
  #checkFormValidity() {
    const form = this.closest('form');
    if (!form) return true;

    const allInputs = Array.from(form.querySelectorAll('input, select, textarea')).filter((input) =>
      input.id.includes('Recipient')
    );
    let allInputsValid = true;
    for (const input of allInputs) {
      if (
        !(
          input instanceof HTMLInputElement ||
          input instanceof HTMLSelectElement ||
          input instanceof HTMLTextAreaElement
        )
      ) {
        continue;
      }

      // Skip disabled inputs
      if (input.disabled) continue;

      // Check validity on all input elements
      if (!input.checkValidity()) {
        allInputsValid = false;
        break;
      }
    }
    return allInputsValid;
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    window.__libernovoTriggerHapticFeedback?.();

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
      const sectionEl = this.closest('section') || document;
      const logAccessoryProductVariant = (source = 'add-to-cart') => {
        const switcherDataEl = sectionEl.querySelector('script[id^="product-context-switcher-data-"]');
        if (!switcherDataEl) return;

        try {
          const data = JSON.parse(switcherDataEl.textContent || '{}');
          const variantId = String(formData.get('id') || data.currentVariantId || '');
          const variant = Array.isArray(data?.variants)
            ? data.variants.find((item) => String(item.id) === variantId) || null
            : null;
          const params = new URLSearchParams(window.location.search);
          const selectedSeries = params.get('series') || '';
          const selectedModel = params.get('model') || '';
          const selectedColor = params.get('color') || data.currentVariantColor || '';
          const selectedCapacity = params.get('capacity') || '';
          const formatAccessoryModel = (value) => {
            const normalizedModel = String(value || '')
              .toLowerCase()
              .replace(/\+/g, ' ')
              .replace(/[_-]+/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();
            if (normalizedModel === 'omni gen') return 'Omni Gen';
            if (normalizedModel === 'omni se') return 'Omni SE';
            if (normalizedModel === 'omni pro') return 'Omni Pro';
            if (normalizedModel === 'maxis manual') return 'Maxis Manual';
            if (normalizedModel === 'maxis electric') return 'Maxis Electric';
            if (normalizedModel === 'maxis airflow') return 'Maxis Airflow';
            if (normalizedModel === 'omni') return 'Omni';
            return String(value || '').trim();
          };
          const formatAccessoryColor = (value) => {
            const normalizedColor = String(value || '')
              .toLowerCase()
              .replace(/\+/g, ' ')
              .replace(/[_-]+/g, ' ')
              .replace(/\s+/g, ' ')
              .trim();
            if (normalizedColor.includes('midnight black') || normalizedColor === 'black') return 'Midnight Black';
            if (normalizedColor.includes('space grey') || normalizedColor.includes('space gray') || normalizedColor === 'grey' || normalizedColor === 'gray') return 'Space Grey';
            if (normalizedColor.includes('moss green') || normalizedColor === 'green') return 'Moss Green';
            if (normalizedColor.includes('obsidian')) return 'Obsidian';
            if (normalizedColor.includes('graphite')) return 'Graphite';
            if (normalizedColor.includes('glacier')) return 'Glacier';
            return String(value || '').trim();
          };
          const matchedVariant = [variant?.option1, variant?.option2, variant?.option3]
            .map((value) => String(value || '').trim())
            .filter(Boolean)
            .join(' + ') || String(variant?.title || '').trim();
          const matchedVariantTitle = String(variant?.title || '').trim() || matchedVariant;
          const matchedVariantDisplayTitle = [
            formatAccessoryModel(selectedModel),
            formatAccessoryColor(selectedColor),
            data.kind === 'battery' && selectedCapacity ? String(selectedCapacity).trim() : '',
          ]
            .filter(Boolean)
            .join(' / ') || matchedVariantTitle;

          if (matchedVariantTitle) {
            formData.set('properties[_matched_variant_title]', matchedVariantTitle);
          }
          if (matchedVariantDisplayTitle) {
            formData.set('properties[_matched_variant_display_title]', matchedVariantDisplayTitle);
          }
          if (String(variant?.sku || '').trim()) {
            formData.set('properties[_matched_variant_sku]', String(variant.sku).trim());
          }

          const summary = [
            `[LiberNovo Accessory] ${source}`,
            `kind="${data.kind || 'accessory'}"`,
            `model="${selectedModel}"`,
            `color="${selectedColor}"`,
            selectedCapacity ? `capacity="${selectedCapacity}"` : '',
            `matched="${matchedVariantDisplayTitle}"`,
            `sku="${String(variant?.sku || '').trim() || '(blank)'}"`,
            `id="${String(variant?.id || variantId || '')}"`,
          ]
            .filter(Boolean)
            .join(' | ');

          console.log(summary);
          console.group(`[LiberNovo Accessory] ${source} details`);
          console.log('Product Kind:', data.kind || 'accessory');
          console.log('Selected Chair Series:', selectedSeries);
          console.log('Selected Chair Model:', selectedModel);
          if (selectedCapacity) {
            console.log('Selected Capacity:', selectedCapacity);
          }
          console.log('Selected Color:', selectedColor);
          console.log('Matched Variant:', matchedVariantDisplayTitle);
          console.log('Resolved Variant Title:', matchedVariantTitle);
          console.log('Variant SKU:', String(variant?.sku || '').trim() || '(blank)');
          console.log('Variant ID:', variant?.id || variantId || '');
          console.log('Variant Title:', variant?.title || '');
          console.groupEnd();
        } catch (error) {
          console.warn('[LiberNovo Accessory] Failed to log accessory add-to-cart debug info', error);
        }
      };
      const bundleFieldset = sectionEl.querySelector('fieldset.variant-option--bundle-plan');
      if (bundleFieldset) {
        const checkedRadio = bundleFieldset.querySelector('input[type="radio"]:checked');
        const selectedBundle = checkedRadio?.value || 'basic bundle';
        if (selectedBundle && selectedBundle !== 'basic bundle') {
          const bundleLabel = (() => {
            const radioLabel = checkedRadio?.closest('.variant-option__button-label');
            const textEl = radioLabel ? radioLabel.querySelector('.variant-option__button-label__text') : null;
            const text = textEl ? (textEl.textContent || '').trim() : '';
            if (text) return text;
            if (selectedBundle.includes('pro')) return 'Pro Bundle';
            if (selectedBundle.includes('standard')) return 'Standard Bundle';
            return 'Basic Bundle';
          })();
          const bundleUuid = `b-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

          // ── ADDED: Get seat depth selected by user ──────────────
          const seatDepth = window.currentSeatDepth ||
            document.querySelector('.seat-depth-btn.is-active')?.getAttribute('data-depth') || '';
          // ────────────────────────────────────────────────────────

          const normalizeText = (value) => String(value || '')
            .toLowerCase()
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
          const normalizeColor = (color) => {
            const lower = normalizeText(color);
            if (!lower) return 'grey';
            if (lower.includes('black') || lower.includes('blk')) return 'black';
            if (lower.includes('grey') || lower.includes('gray')) return 'grey';
            if (lower.includes('green') || lower.includes('moss')) return 'green';
            if (lower.includes('obsidian')) return 'obsidian';
            if (lower.includes('graphite')) return 'graphite';
            if (lower.includes('glacier')) return 'glacier';
            return lower;
          };
          const canonicalColorName = (color) => {
            const normalized = normalizeColor(color);
            if (normalized === 'black') return 'jet black';
            if (normalized === 'grey') return 'silver grey';
            if (normalized === 'green') return 'moss green';
            return normalized;
          };
          const KNOWN_COLOR_BUCKETS = new Set(['black', 'grey', 'green', 'obsidian', 'graphite', 'glacier']);
          const COLOR_OPTION_ALIASES = ['Color', 'Colour', '×¦×‘×¢'];
          const COMPATIBILITY_OPTION_ALIASES = [
            'Compatible Chair Group',
            'Compatible Group',
            'Compatible Chair Model',
            'Compatible Model',
            'Chair Model',
            'Model',
          ];
          const CAPACITY_OPTION_ALIASES = ['Capacity', 'Battery Capacity'];
          const normalizeChairModel = (value) => {
            const lower = normalizeText(value);
            if (!lower) return '';
            if (lower.includes('omni gen')) return 'omni gen';
            if (lower.includes('omni se')) return 'omni se';
            if (lower.includes('omni pro')) return 'omni pro';
            if (lower.includes('maxis manual') || lower === 'manual') return 'maxis manual';
            if (lower.includes('maxis electric') || lower === 'electric') return 'maxis electric';
            if (lower.includes('maxis airflow') || lower === 'airflow') return 'maxis airflow';
            if (lower === 'omni' || lower.startsWith('omni ')) return 'omni';
            return lower;
          };
          const normalizeCapacity = (value) => {
            const lower = normalizeText(value);
            if (!lower) return '';
            if (lower.includes('3000')) return '3000mah';
            if (lower.includes('2200')) return '2200mah';
            return lower.replace(/\s+/g, '');
          };
          const splitCompatibilityTokens = (value) => String(value || '')
            .split(/\s*(?:\/|\+|,|&)\s*/g)
            .map((item) => normalizeChairModel(item))
            .filter(Boolean);
          const getSelectedChairModel = () => {
            const activeModelLabel = sectionEl.querySelector(
              '.chair-model-product-switcher .cmps__button.is-active .cmps__button-label, .chair-model-product-switcher .cmps__button[aria-current="page"] .cmps__button-label'
            );
            const pickerHandle = sectionEl.querySelector('variant-picker[data-handle]')?.dataset.handle || '';
            const source = normalizeText(`${activeModelLabel?.textContent || ''} ${pickerHandle} ${window.location.pathname}`);
            if (source.includes('omni gen')) return 'omni gen';
            if (source.includes('omni se')) return 'omni se';
            if (source.includes('omni pro')) return 'omni pro';
            if (source.includes('maxis manual')) return 'maxis manual';
            if (source.includes('maxis electric')) return 'maxis electric';
            if (source.includes('maxis airflow')) return 'maxis airflow';
            return 'omni';
          };
          const getBatteryCapacityForModel = (model) => {
            const normalizedModel = normalizeChairModel(model);
            if (normalizedModel === 'omni') return '2200mah';
            if (normalizedModel === 'omni gen') return '2200mah';
            if (normalizedModel === 'omni pro') return '3000mah';
            if (normalizedModel === 'maxis electric') return '2200mah';
            if (normalizedModel === 'maxis airflow') return '3000mah';
            return '';
          };
          const formatDisplayModel = (model) => {
            const normalizedModel = normalizeChairModel(model);
            if (normalizedModel === 'omni') return 'Omni';
            if (normalizedModel === 'omni gen') return 'Omni Gen';
            if (normalizedModel === 'omni se') return 'Omni SE';
            if (normalizedModel === 'omni pro') return 'Omni Pro';
            if (normalizedModel === 'maxis manual') return 'Maxis Manual';
            if (normalizedModel === 'maxis electric') return 'Maxis Electric';
            if (normalizedModel === 'maxis airflow') return 'Maxis Airflow';
            return String(model || '').trim();
          };
          const formatDisplayColor = (color) => {
            const normalizedColor = normalizeColor(color);
            if (normalizedColor === 'black') return 'Midnight Black';
            if (normalizedColor === 'grey') return 'Space Grey';
            if (normalizedColor === 'green') return 'Moss Green';
            if (normalizedColor === 'obsidian') return 'Obsidian';
            if (normalizedColor === 'graphite') return 'Graphite';
            if (normalizedColor === 'glacier') return 'Glacier';
            return String(color || '').trim();
          };
          const inferAccessoryContext = () => {
            const model = getSelectedChairModel();
            return {
              model,
              batteryCapacity: getBatteryCapacityForModel(model),
            };
          };
          const getSelectedColor = () => {
            const variantJsonEl = sectionEl.querySelector('variant-picker script[type="application/json"]');
            if (variantJsonEl) {
              try {
                const variantObj = JSON.parse(variantJsonEl.textContent || '{}');
                if (variantObj && Array.isArray(variantObj.options)) {
                  const opt = variantObj.options.find(o => String(o.name || '').toLowerCase().includes('color'));
                  if (opt && opt.value) return String(opt.value);
                }
                if (variantObj && variantObj.metafields && variantObj.metafields.custom && variantObj.metafields.custom.color) {
                  return String(variantObj.metafields.custom.color);
                }
              } catch(_) {}
            }
            const colorInputs = sectionEl.querySelectorAll('fieldset.variant-option--swatches input[type="radio"], input[name*="color"], input[name*="Color"], input[name*="colour"]');
            const checkedColor = Array.from(colorInputs).find(i => i instanceof HTMLInputElement && i.checked);
            if (checkedColor instanceof HTMLInputElement) return checkedColor.value;
            const activeLabel = sectionEl.querySelector('fieldset.variant-option--swatches .variant-option__button-label[aria-pressed="true"]');
            if (activeLabel) return activeLabel.textContent || '';
            if (window.currentProductColor) return String(window.currentProductColor);
            const mfEl = document.getElementById('pdp-color-metafield');
            if (mfEl) {
              try {
                const mfColor = JSON.parse(mfEl.textContent || '""');
                if (mfColor) return String(mfColor);
              } catch(_) {}
            }
            return 'grey';
          };
          const accessoryDataEl = sectionEl.querySelector('script[id^="bundle-accessory-data-"]');
          if (accessoryDataEl) {
            const accessoryData = JSON.parse(accessoryDataEl.textContent || '{}');
            const items = [];
            const mainVariantId = formData.get('id');
            const quantity = formData.get('quantity') || 1;
            if (mainVariantId) {
              const mainProperties = {
                _bundle_uuid: bundleUuid,
                _bundle_label: bundleLabel,
                _main_chair_product: 'true'
              };

              // Capture all properties from formData
              for (const [key, value] of formData.entries()) {
                if (key.startsWith('properties[')) {
                  const propKey = key.substring(11, key.length - 1);
                  mainProperties[propKey] = value;
                }
              }

              // Double check for all property inputs in the form
              form.querySelectorAll('[name^="properties["]').forEach(input => {
                const name = input.getAttribute('name');
                const match = name.match(/properties\[(.*?)\]/);
                if (match && match[1]) {
                  const val = input.value || '';
                  if (val) {
                    mainProperties[match[1]] = val;
                  }
                }
              });

              // Specific fix for delivery property which might be outside the main form scope in some layouts
              const deliveryInput = sectionEl.querySelector('[data-delivery-property]');
              if (deliveryInput && deliveryInput.value) {
                mainProperties['זמן אספקה'] = deliveryInput.value;
              }

              // ── ADDED: seat depth + bundle name for checkout ────
              if (seatDepth) mainProperties['Seat Depth'] = seatDepth;
              mainProperties['Bundle'] = bundleLabel;
              
              // NEW: Attempt to get Newton variant ID for checkout redirect
              try {
                const newtonHandle = productUrl.split('/').pop().split('?').shift();
                mainProperties['_newton_handle'] = newtonHandle;
              } catch(_) {}

              // ────────────────────────────────────────────────────

              const preorderFlag = formData.get('properties[_libernovo_preorder]');
              //const preorderFlagPublic = formData.get('properties[pre-order]');
              console.log('Bundle: Preorder flags detected:', { internal: preorderFlag });
              if (preorderFlag) {
                mainProperties._libernovo_preorder = String(preorderFlag);
              }
              //const preorderFlagPublic = formData.get('properties[pre-order]');
              //if (preorderFlagPublic) {
                //mainProperties['pre-order'] = String(preorderFlagPublic);
              //}
              // FALLBACK: If flags are missing but product has pre-order tag (checked via hidden inputs existence check fail)
              // We check if the form *should* have had them. Since we can't easily check liquid tags here, 
              // we rely on the hidden inputs. If they are missing, it might be a caching issue or liquid logic failure.
              // However, we can also forcefully inject them if we are SURE this is a pre-order product.
              // For now, let's rely on the inputs being present in the DOM even if FormData missed them (unlikely).
              if (!preorderFlag) {
                 const hiddenInputInternal = form.querySelector('input[name="properties[_libernovo_preorder]"]');
                 //const hiddenInputPublic = form.querySelector('input[name="properties[pre-order]"]');
                 if (hiddenInputInternal) mainProperties._libernovo_preorder = hiddenInputInternal.value;
                 //if (hiddenInputPublic) mainProperties['pre-order'] = hiddenInputPublic.value;
              }
              items.push({
                id: parseInt(String(mainVariantId)),
                quantity: parseInt(String(quantity)),
                properties: mainProperties
              });
            }
          let selectedColor = getSelectedColor();
          if (window.currentProductColor) {
            selectedColor = String(window.currentProductColor);
          }
          const selectedChairModelRaw = getSelectedChairModel();
          const selectedChairModelDisplay = formatDisplayModel(selectedChairModelRaw);
          const selectedAccessoryColorDisplay = formatDisplayColor(selectedColor);
          const selectedBatteryCapacityDisplay = getBatteryCapacityForModel(selectedChairModelRaw);
          try {
            console.log('Bundle: Selected color', selectedColor, 'normalized', normalizeColor(selectedColor));
          } catch(_) {}
          const getOptionIndex = (product, aliases) => {
            const normalizedAliases = aliases.map((alias) => normalizeText(alias)).filter(Boolean);
            const optionNames = Array.isArray(product?.option_names) ? product.option_names : [];
            for (let index = 0; index < optionNames.length; index++) {
              const optionName = normalizeText(optionNames[index]);
              if (normalizedAliases.some((alias) => optionName === alias || optionName.includes(alias) || alias.includes(optionName))) {
                return index;
              }
            }
            return -1;
          };
          const getVariantOptionValue = (product, variant, aliases) => {
            const optionIndex = getOptionIndex(product, aliases);
            if (optionIndex === -1) return '';
            const optionValues = Array.isArray(variant?.options) ? variant.options : [variant?.option1, variant?.option2, variant?.option3];
            return optionValues[optionIndex] || '';
          };
          const hasCompatibilityOption = (product) => getOptionIndex(product, COMPATIBILITY_OPTION_ALIASES) !== -1;
          const hasCapacityOption = (product) => getOptionIndex(product, CAPACITY_OPTION_ALIASES) !== -1;
          const getVariantCompatibilityValue = (product, variant) => {
            const compatibilityValue = getVariantOptionValue(product, variant, COMPATIBILITY_OPTION_ALIASES);
            if (compatibilityValue) return String(compatibilityValue);
            if (Array.isArray(product?.option_names) && product.option_names.length) return '';
            const fallbackValue = [variant?.option1, variant?.option2, variant?.option3]
              .map((value) => String(value || '').trim())
              .find((value) => value && !KNOWN_COLOR_BUCKETS.has(normalizeColor(value)));
            return fallbackValue || '';
          };
          const getVariantCompatibilityTokens = (product, variant) =>
            splitCompatibilityTokens(getVariantCompatibilityValue(product, variant));
          const getVariantColor = (product, variant) => {
            const directColor = getVariantOptionValue(product, variant, COLOR_OPTION_ALIASES);
            if (directColor) return String(directColor);
            const fallbackColor = [variant?.option1, variant?.option2, variant?.option3]
              .map((value) => String(value || '').trim())
              .find((value) => value && KNOWN_COLOR_BUCKETS.has(normalizeColor(value)));
            if (fallbackColor) return fallbackColor;
            return String(variant?.metafield_color || variant?.title || '');
          };
          const getVariantCapacity = (product, variant) =>
            String(getVariantOptionValue(product, variant, CAPACITY_OPTION_ALIASES) || '');
          const findVariantId = (productKey, color) => {
            const product = accessoryData[productKey];
            if (!product || !product.variants || !product.variants.length) {
              console.log(`Bundle: No variants found for ${productKey}`);
              return null;
            }
            const targetColor = normalizeColor(color);
            const canonical = canonicalColorName(color);
            console.log(`Bundle: Finding ${productKey} variant for color "${color}" (normalized: "${targetColor}", canonical: "${canonical}")`);
            
            // Log all available variants for debugging
            console.log(`Bundle: Available ${productKey} variants:`, product.variants.map(v => ({
              id: v.id,
              title: v.title,
              option1: v.option1,
              option2: v.option2,
              option3: v.option3,
              option_names: product.option_names,
              metafield_color: v.metafield_color,
              available: v.available
            })));

            // First try exact match on normalized color
            let match = product.variants.find((v) => {
              const colorFromOption = getVariantOptionValue(product, v, ['Color', 'Colour', 'צבע']);
              const title = String(v.title || '').toLowerCase();
              const opt1 = String(v.option1 || '');
              const mf = String(v.metafield_color || '');
              
              // Check all possible color sources, normalized
              const colorFromOptionNorm = normalizeColor(colorFromOption);
              const opt1Norm = normalizeColor(opt1);
              const mfNorm = normalizeColor(mf);
              const titleNorm = normalizeColor(title);
              
              const matches = colorFromOptionNorm === targetColor || 
                              opt1Norm === targetColor || 
                              mfNorm === targetColor || 
                              titleNorm === targetColor ||
                              opt1.toLowerCase() === canonical.toLowerCase() ||
                              title === canonical.toLowerCase();
              
              if (matches) {
                console.log(`Bundle: Found exact match for ${productKey}:`, v.title, v.id);
              }
              
              return matches && v.available;
            });
            
            // If no exact match found, try any available variant that includes the color
            if (!match) {
              console.log(`Bundle: No exact available match, trying partial matches for ${productKey}`);
              match = product.variants.find((v) => {
                const colorFromOption = getVariantOptionValue(product, v, ['Color', 'Colour', 'צבע']);
                const title = String(v.title || '').toLowerCase();
                const opt1 = String(v.option1 || '');
                const mf = String(v.metafield_color || '');
                
                return (
                  normalizeColor(colorFromOption).includes(targetColor) || targetColor.includes(normalizeColor(colorFromOption)) ||
                  normalizeColor(opt1).includes(targetColor) || targetColor.includes(normalizeColor(opt1)) ||
                  normalizeColor(mf).includes(targetColor) || targetColor.includes(normalizeColor(mf)) ||
                  title.includes(canonical.toLowerCase())
                ) && v.available;
              });
            }
            
            // If still no match, use first available or first variant, ignoring availability for exact match
            if (!match) {
              console.log(`Bundle: No available match, trying exact match on any variant`);
              match = product.variants.find((v) => {
                const colorFromOption = getVariantOptionValue(product, v, ['Color', 'Colour', 'צבע']);
                const title = String(v.title || '').toLowerCase();
                const opt1 = String(v.option1 || '');
                const mf = String(v.metafield_color || '');
                
                const colorFromOptionNorm = normalizeColor(colorFromOption);
                const opt1Norm = normalizeColor(opt1);
                const mfNorm = normalizeColor(mf);
                const titleNorm = normalizeColor(title);
                
                return colorFromOptionNorm === targetColor || 
                       opt1Norm === targetColor || 
                       mfNorm === targetColor || 
                       titleNorm === targetColor;
              });
            }
            
            // If still nothing, use first available or first
            if (!match) {
              console.log(`Bundle: No match found, using first available variant for ${productKey}`);
              match = product.variants.find((v) => v.available) || product.variants[0];
            }
            
            if (match) {
              console.log(`Bundle: Final matched ${productKey} variant:`, match.id, match.title);
            }
            return match ? match.id : null;
          };
          const findVariantMatch = (productKey, color) => {
            const product = accessoryData[productKey];
            if (!product || !product.variants || !product.variants.length) {
              console.log(`Bundle: No variants found for ${productKey}`);
              return null;
            }

            const context = inferAccessoryContext();
            const targetColor = normalizeColor(color);
            const targetModel = normalizeChairModel(context.model);
            const targetCapacity = normalizeCapacity(context.batteryCapacity);
            const requiresCompatibility = hasCompatibilityOption(product);
            const requiresCapacity = hasCapacityOption(product) && Boolean(targetCapacity);

            let bestMatch = null;
            let bestScore = -Infinity;

            product.variants.forEach((variant) => {
              const variantColor = normalizeColor(getVariantColor(product, variant));
              if (targetColor && variantColor !== targetColor) return;

              const compatibilityTokens = getVariantCompatibilityTokens(product, variant);
              if (requiresCompatibility && targetModel) {
                if (!compatibilityTokens.includes(targetModel)) return;
              }

              const variantCapacity = normalizeCapacity(getVariantCapacity(product, variant));
              if (requiresCapacity) {
                if (!variantCapacity || variantCapacity !== targetCapacity) return;
              }

              let score = 0;
              if (targetColor && variantColor === targetColor) score += 200;
              if (targetModel && compatibilityTokens.includes(targetModel)) score += 120;
              if (requiresCapacity && variantCapacity === targetCapacity) score += 60;
              if (variant.available) score += 5;

              if (score > bestScore) {
                bestScore = score;
                bestMatch = {
                  ...variant,
                  compatibilityValue: getVariantCompatibilityValue(product, variant),
                };
              }
            });

            if (bestMatch) {
              console.log(`Bundle: Matched ${productKey} variant`, {
                model: targetModel,
                color: targetColor,
                compatibility: bestMatch.compatibilityValue,
                title: bestMatch.title,
                id: bestMatch.id,
              });
              return bestMatch;
            }

            if (!requiresCompatibility) {
              const legacyMatch = product.variants.find((variant) => normalizeColor(getVariantColor(product, variant)) === targetColor && variant.available)
                || product.variants.find((variant) => normalizeColor(getVariantColor(product, variant)) === targetColor)
                || product.variants.find((variant) => variant.available)
                || product.variants[0]
                || null;
              return legacyMatch ? { ...legacyMatch, compatibilityValue: '' } : null;
            }

            return null;
          };
          const findVariantImageSrc = (productKey, variantId) => {
            const product = accessoryData[productKey];
            if (!product || !product.variants || !product.variants.length || !variantId) return '';
            const match = product.variants.find((v) => String(v.id) === String(variantId));
            if (!match) return '';
            return String(match.image || match.featured_media || match.featured_image || '').trim();
          };
          if (selectedBundle.includes('standard') || selectedBundle.includes('pro')) {
            const footVariant = findVariantMatch('foot_support', selectedColor);
            if (footVariant) items.push({
              id: footVariant.id,
              quantity: 1,
              properties: {
                _chair_accessory: 'true',
                _accessory_name: 'Foot Support',
                _linked_to: String(mainVariantId),
                _color: String(selectedColor),
                _matched_variant_title: String(footVariant.title || ''),
                _matched_variant_display_title: [selectedChairModelDisplay, selectedAccessoryColorDisplay].filter(Boolean).join(' / ') || String(footVariant.title || ''),
                _accessory_image: findVariantImageSrc('foot_support', footVariant.id),
                _compatibility_group: footVariant.compatibilityValue || '',
                _bundle_uuid: bundleUuid,
                _bundle_label: bundleLabel,
                // ── ADDED ──
                ...(seatDepth ? { 'Seat Depth': seatDepth } : {}),
                'Bundle': bundleLabel,
                '_newton_handle': 'libernovo-foot-support', // Newton accessory handle
                // ───────────
              }
            });
          }
          if (selectedBundle.includes('pro')) {
            const batteryVariant = findVariantMatch('battery', selectedColor);
            if (batteryVariant) items.push({
              id: batteryVariant.id,
              quantity: 1,
              properties: {
                _chair_accessory: 'true',
                _accessory_name: 'Battery',
                _linked_to: String(mainVariantId),
                _color: String(selectedColor),
                _matched_variant_title: String(batteryVariant.title || ''),
                _matched_variant_display_title: [selectedChairModelDisplay, selectedAccessoryColorDisplay, selectedBatteryCapacityDisplay].filter(Boolean).join(' / ') || String(batteryVariant.title || ''),
                _accessory_image: findVariantImageSrc('battery', batteryVariant.id),
                _compatibility_group: batteryVariant.compatibilityValue || '',
                _bundle_uuid: bundleUuid,
                _bundle_label: bundleLabel,
                ...(selectedBatteryCapacityDisplay ? { 'Capacity': selectedBatteryCapacityDisplay } : {}),
                // ── ADDED ──
                ...(seatDepth ? { 'Seat Depth': seatDepth } : {}),
                'Bundle': bundleLabel,
                '_newton_handle': 'libernovo-battery', // Newton accessory handle
                // ───────────
              }
            });
          }
          fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
          })
            .then((r) => r.json())
            .then(() => {
              const cartDrawer = document.querySelector('cart-drawer-component');
              const cartItemsComponent = cartDrawer?.querySelector('cart-items-component');
              const sid = cartItemsComponent?.dataset.sectionId;
              const openAndGroup = () => {
                if (typeof window.safeOpenCartDrawer === 'function') {
                  window.safeOpenCartDrawer();
                  return;
                }
                if (cartDrawer && typeof cartDrawer.open === 'function') {
                  cartDrawer.open();
                  setTimeout(() => {
                    if (typeof window.groupCartItems === 'function') {
                      window.groupCartItems();
                    }
                  }, 150);
                }
              };
              if (sid) {
                fetch(`/?section_id=${sid}`)
                  .then((resp) => resp.text())
                  .then((html) => {
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(html, 'text/html');
                    const newContent = doc.querySelector('cart-items-component');
                    if (newContent && cartItemsComponent?.parentNode) {
                      cartItemsComponent.parentNode.replaceChild(newContent, cartItemsComponent);
                    }
                    // Fire cart updated and then open + group
                    try {
                      const sections = {};
                      sections[sid] = html;
                      this.dispatchEvent(
                        new CartAddEvent({}, this.id, {
                          source: 'product-form-component',
                          sections,
                        })
                      );
                    } catch(_) {}
                    openAndGroup();
                  })
                  .catch(() => {
                    openAndGroup();
                  });
              } else {
                openAndGroup();
              }
            })
            .catch((_e) => {})
            .finally(() => {
              cartPerformance.measureFromEvent('add:user-action', event);
            });
          return;
        }
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    logAccessoryProductVariant();

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
